/**
 * Anchor Bullion — Silver Price Page JavaScript
 * Live XAG spot, calculators, currency tabs, sticky pill, and UX helpers.
 * SEO/SGE-friendly: stable IDs/labels, accurate timestamping (ET), and robust parsing.
 */

(function () {
  'use strict';

  // =========================
  // Constants
  // =========================
  const TROY_OZ_TO_GRAM = 31.1034768;
  const TROY_OZ_TO_KILO = 0.0311034768;

  // Toggle for development vs production refresh cadence
  const TESTING_MODE = false; // ← set to false in production
  const REFRESH_INTERVAL = TESTING_MODE ? 3600000 : 60000; // 1 hour test, 60s prod

  // Storage keys scoped to SILVER
  const STORAGE_KEY = 'ab_silver_currency';
  const PRICE_CACHE_KEY = 'ab_silver_price_cache';
  const CACHE_DURATION = TESTING_MODE ? 3600000 : 60000; // 1 hour test cache, 1 min prod

  // =========================
  // State
  // =========================
  let currentPrices = {
    bid: 29.10,
    ask: 29.14,
    mid: 29.12,
    change: +0.08,
    changePct: +0.28,
    prevClose: 29.04
  };

  // FX quoted as pairs vs USD (same as gold page for consistency)
  let currentFX = {
    EURUSD: 1.085,
    GBPUSD: 1.285,
    USDJPY: 154.1,
    USDCAD: 1.34,
    AUDUSD: 0.66
  };

  let selectedCurrency = 'USD';
  let apiUrl = '';
  let refreshTimer = null;
  let retryCount = 0;
  let retryDelay = 1000;

  // =========================
  // DOM helpers
  // =========================
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const byId = (id) => document.getElementById(id);

  function getSection() {
    // Primary selector is the silver section; fallbacks make it resilient during integration
    return $('.ab-silver-price-section') ||
           $('[data-metal="silver"]') ||
           $('.ab-price-section--silver') ||
           document.body; // last resort
  }

  function getDom() {
    const section = getSection();

    const card = section?.querySelector('.ab-price-card') || $('.ab-price-card');

    return {
      section,
      // Card regions
      card,
      loading: card?.querySelector('.ab-price-loading'),
      content: card?.querySelector('.ab-price-content'),
      error: card?.querySelector('.ab-price-error'),

      // Main spot values
      priceBid: $('[data-price="bid"]', section) || byId('ab-bid-price'),
      priceMid: $('[data-price="mid"]', section) || byId('ab-mid-price'),
      priceAsk: $('[data-price="ask"]', section) || byId('ab-ask-price'),

      // Per-unit tiles
      unitOz: $('[data-unit="oz"], [data-unit="ounce"], [data-unit="per-ounce"]', section) || byId('ab-oz-price'),
      unitGram: $('[data-unit="gram"]', section) || byId('ab-gram-price'),
      unitKilo: $('[data-unit="kilo"], [data-unit="kg"]', section) || byId('ab-kilo-price'),

      // Changes
      changeAmt: $('[data-change="amount"]', section) || byId('ab-price-change'),
      changePct: $('[data-change="percent"]', section),

      // Timestamp
      lastUpdatedTime: $('time[data-timestamp]', section) || byId('ab-update-time'),

      // Currency UI
      currencySelect: $('.ab-currency-select', section) || byId('ab-currency-selector'),
      currencyTabs: $$('.ab-currency-tabs [data-currency]', section),

      // Table body
      priceTableBody: byId('ab-silver-price-tbody') || $('.ab-silver-price-table tbody', section),

      // Sticky pill
      stickyPill: byId('ab-sticky-pill') || $('.ab-sticky-pill'),
      stickyPrice: $('[data-sticky-price]', section) || byId('ab-pill-price'),
      stickyChange: $('[data-sticky-change]', section) || byId('ab-pill-change'),

      // Calculators outputs
      calcOut: {
        portfolioBid: $('[data-calc="portfolio-bid"]', section) || byId('ab-portfolio-bid'),
        portfolioAsk: $('[data-calc="portfolio-ask"]', section) || byId('ab-portfolio-ask'),
        fineMetal: $('[data-calc="fine-silver"]', section) || byId('ab-scrap-fine'),
        scrapValue: $('[data-calc="scrap-value"]', section) || byId('ab-scrap-value'),
        premiumAmount: $('[data-calc="premium-amount"]', section) || byId('ab-premium-amount'),
        finalPrice: $('[data-calc="final-price"]', section) || byId('ab-premium-final')
      },

      // Calculator inputs (robust selectors)
      inputs: {
        // Portfolio
        portfolioAmount: byId('ab-portfolio-amount'),
        portfolioUnit: byId('ab-portfolio-unit'),
        // Scrap (silver purity)
        scrapWeight: byId('ab-scrap-weight') || byId('ab-jewelry-weight'),
        scrapUnit: byId('ab-scrap-unit') || byId('ab-jewelry-unit'),
        scrapPurity: byId('ab-scrap-purity') || byId('ab-silver-purity'),
        // Premium
        spotInput: byId('ab-premium-spot') || byId('spot-price'),
        premiumPercent: byId('ab-premium-percent')
      },

      // Alert
      alertTrigger: byId('ab-alert-trigger'),
      alertContainer: byId('ab-alert-container'),
      alertForm: byId('ab-alert-form'),
      alertCancel: byId('ab-alert-cancel')
    };
  }

  function togglePriceUI(state) {
    const d = getDom();
    if (!d.card) return;
    if (d.loading) d.loading.style.display = state === 'loading' ? '' : 'none';
    if (d.content) d.content.style.display = state === 'ready' ? '' : 'none';
    if (d.error) d.error.style.display = state === 'error' ? '' : 'none';
  }

  // =========================
  // Init
  // =========================
  function init() {
    togglePriceUI('loading');

    const section = getSection();
    if (section) {
      apiUrl =
        section.dataset.apiUrl ||
        window.AB_SILVER_API_URL || // optional global injection
        '';
    }

    // Saved currency preference
    const savedCurrency = localStorage.getItem(STORAGE_KEY);
    if (savedCurrency && ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'].includes(savedCurrency)) {
      selectedCurrency = savedCurrency;
    }

    // Load cached values if fresh
    loadCachedPrices();

    // UI hookup
    setupEventListeners();
    updateCurrencySelector();
    updateAllPrices();

    // Calculators + sticky pill
    setupCalculators();
    setupStickyPill();

    // Fetch if API configured
    if (apiUrl) {
      if (shouldFetchNewPrices()) {
        fetchPrices();
      } else {
        togglePriceUI('ready');
        updateLastUpdatedTime();
      }
      startPriceRefresh();
    } else {
      console.warn('No API URL configured for silver price updates');
      showApiError();
    }

    if (TESTING_MODE) showTestingModeIndicator();
  }

  // =========================
  // Cache helpers
  // =========================
  function shouldFetchNewPrices() {
    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    if (!cache) return true;
    try {
      const { timestamp } = JSON.parse(cache);
      return Date.now() - timestamp > CACHE_DURATION;
    } catch {
      return true;
    }
  }

  function loadCachedPrices() {
    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    if (!cache) return false;
    try {
      const cacheData = JSON.parse(cache);
      const cacheAge = Date.now() - cacheData.timestamp;
      if (cacheAge > CACHE_DURATION) return false;
      if (cacheData.prices) currentPrices = { ...currentPrices, ...cacheData.prices };
      if (cacheData.fx) currentFX = { ...currentFX, ...cacheData.fx };
      return true;
    } catch {
      return false;
    }
  }

  function savePricesToCache() {
    try {
      localStorage.setItem(
        PRICE_CACHE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          prices: currentPrices,
          fx: currentFX
        })
      );
    } catch {}
  }

  function getTimeUntilNextFetch() {
    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    if (!cache) return 'now';
    try {
      const { timestamp } = JSON.parse(cache);
      const ms = CACHE_DURATION - (Date.now() - timestamp);
      if (ms <= 0) return 'now';
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      return m > 0 ? `${m}m ${s}s` : `${s}s`;
    } catch {
      return 'now';
    }
  }

  // =========================
  // Testing UI (visible only in TESTING_MODE)
  // =========================
  function showTestingModeIndicator() {
    const indicator = document.createElement('div');
    indicator.id = 'ab-testing-indicator';
    indicator.style.cssText = [
      'position:fixed',
      'top:10px',
      'right:10px',
      'background:#fbbf24',
      'color:#78350f',
      'padding:8px 16px',
      'border-radius:20px',
      'font-size:14px',
      'font-weight:600',
      'z-index:10000',
      'box-shadow:0 2px 8px rgba(0,0,0,.2)'
    ].join(';');
    indicator.innerHTML = `
      <span>🧪 Testing Mode (Silver): API ~1/hr</span>
      <button id="ab-force-refresh" style="margin-left:10px;padding:2px 8px;background:#78350f;color:#fff;border:none;border-radius:3px;cursor:pointer">Force Refresh</button>
    `;
    document.body.appendChild(indicator);

    const btn = byId('ab-force-refresh');
    if (btn) {
      btn.addEventListener('click', () => {
        localStorage.removeItem(PRICE_CACHE_KEY);
        fetchPrices();
      });
      setInterval(() => (btn.title = `Next auto update in: ${getTimeUntilNextFetch()}`), 1000);
    }

    // Minimal toast styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes abSlideIn { from { transform: translateX(100%); opacity:0; } to { transform: translateX(0); opacity:1; } }
      @keyframes abSlideOut { from { transform: translateX(0); opacity:1; } to { transform: translateX(100%); opacity:0; } }
    `;
    document.head.appendChild(style);
  }

  function showNotification(message, type = 'info') {
    const el = document.createElement('div');
    el.style.cssText = `
      position:fixed;bottom:20px;right:20px;padding:12px 20px;
      background:${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color:#fff;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,.2);z-index:10001;
      animation:abSlideIn .3s ease
    `;
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.animation = 'abSlideOut .3s ease';
      setTimeout(() => el.remove(), 300);
    }, 3000);
  }

  // =========================
  // Event listeners
  // =========================
  function setupEventListeners() {
    const d = getDom();

    // Price alert (if present)
    if (d.alertTrigger && d.alertContainer) {
      d.alertTrigger.addEventListener('click', () => {
        d.alertContainer.style.display = d.alertContainer.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (d.alertCancel && d.alertContainer) {
      d.alertCancel.addEventListener('click', () => (d.alertContainer.style.display = 'none'));
    }
    if (d.alertForm) {
      d.alertForm.addEventListener('submit', handleAlertSubmit);
    }

    // Currency select
    if (d.currencySelect) d.currencySelect.addEventListener('change', handleCurrencyChange);

    // Currency tabs
    d.currencyTabs.forEach((btn) =>
      btn.addEventListener('click', () => {
        selectedCurrency = btn.dataset.currency;
        localStorage.setItem(STORAGE_KEY, selectedCurrency);
        setActiveCurrencyTab(selectedCurrency);
        updatePriceTable();
      })
    );

    // Chart period tabs (if present)
    $$('.ab-chart-periods [role="tab"]', getSection()).forEach((tab) => {
      tab.addEventListener('click', () => {
        $$('.ab-chart-periods [role="tab"]').forEach((t) => t.setAttribute('aria-selected', 'false'));
        tab.setAttribute('aria-selected', 'true');
        // Hook for your charting lib
        // onRangeChange(tab.dataset.range);
      });
    });
  }

  // =========================
  // Fetch prices
  // =========================
  async function fetchPrices() {
    if (!apiUrl) return;

    if (TESTING_MODE && !shouldFetchNewPrices()) {
      togglePriceUI('ready');
      return;
    }

    const started = Date.now();
    try {
      const response = await fetch(apiUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      // --- Robust parsing for multiple API shapes ---
      // Shape A (Pipedream): { metals: { silver: { bid, ask, mid, prevCloseUSD, change, changePct } }, fx: {...} }
      if (data && data.metals && data.metals.silver) {
        const s = data.metals.silver || {};
        const bid = num(s.bid, currentPrices.bid);
        const ask = num(s.ask, currentPrices.ask);
        const mid = isNum(s.mid) ? s.mid : (isNum(bid) && isNum(ask) ? (bid + ask) / 2 : currentPrices.mid);

        currentPrices.bid = bid;
        currentPrices.ask = ask;
        currentPrices.mid = mid;

        if (isNum(s.prevCloseUSD)) currentPrices.prevClose = s.prevCloseUSD;
        if (isNum(s.change)) currentPrices.change = s.change;
        else if (isNum(currentPrices.prevClose)) currentPrices.change = mid - currentPrices.prevClose;

        if (isNum(s.changePct)) currentPrices.changePct = s.changePct;
        else if (isNum(currentPrices.prevClose) && currentPrices.prevClose !== 0) {
          currentPrices.changePct = (currentPrices.change / currentPrices.prevClose) * 100;
        }

        // FX if present
        if (data.fx && typeof data.fx === 'object') Object.assign(currentFX, data.fx);

      // Shape B (legacy/alt): { xagUSD: { bid, ask, mid, change, changePct }, prevCloseUSD, fx }
      } else {
        const node =
          data.xagUSD || data.XAGUSD || data.silverUSD || data.silver || data.XAG || null;

        if (node) {
          const bid = num(node.bid, currentPrices.bid);
          const ask = num(node.ask, currentPrices.ask);
          const mid = isNum(node.mid) ? node.mid : (isNum(bid) && isNum(ask) ? (bid + ask) / 2 : currentPrices.mid);

          currentPrices.bid = bid;
          currentPrices.ask = ask;
          currentPrices.mid = mid;

          if (isNum(node.change)) {
            currentPrices.change = node.change;
          } else if (isNum(data.prevCloseUSD)) {
            currentPrices.change = mid - data.prevCloseUSD;
          }

          if (isNum(node.changePct)) {
            currentPrices.changePct = node.changePct;
          } else if (isNum(data.prevCloseUSD) && data.prevCloseUSD !== 0) {
            currentPrices.changePct = ((currentPrices.change / data.prevCloseUSD) * 100);
          }

          if (isNum(data.prevCloseUSD)) currentPrices.prevClose = data.prevCloseUSD;

        } else {
          // Shape C (primitive fields at root)
          if (isNum(data.bid)) currentPrices.bid = data.bid;
          if (isNum(data.ask)) currentPrices.ask = data.ask;
          if (isNum(data.mid)) currentPrices.mid = data.mid;
          if (!isNum(data.mid) && isNum(currentPrices.bid) && isNum(currentPrices.ask)) {
            currentPrices.mid = (currentPrices.bid + currentPrices.ask) / 2;
          }
          if (isNum(data.change)) currentPrices.change = data.change;
          if (isNum(data.changePct)) currentPrices.changePct = data.changePct;
          if (isNum(data.prevCloseUSD)) currentPrices.prevClose = data.prevCloseUSD;
        }

        // FX if present
        if (data.fx && typeof data.fx === 'object') Object.assign(currentFX, data.fx);
      }
      // --- end parsing ---

      // Cache + render
      savePricesToCache();
      updateAllPrices();
      updateLastUpdatedTime();
      togglePriceUI('ready');

      // Analytics / custom hooks
      dispatchUpdateEvent();

      // Reset retries
      retryCount = 0;
      retryDelay = 1000;

      if (TESTING_MODE) showNotification(`✅ Silver prices updated in ${Date.now() - started}ms`, 'success');
    } catch (err) {
      console.error('[API] Silver fetch error:', err);
      handleFetchError();
    }
  }

  function handleFetchError() {
    retryCount++;
    const maxRetries = TESTING_MODE ? 2 : 5;
    if (retryCount <= maxRetries) {
      retryDelay = Math.min(retryDelay * 2, TESTING_MODE ? 60000 : 30000);
      if (TESTING_MODE) showNotification(`⚠️ API error. Retrying in ${Math.round(retryDelay / 1000)}s`, 'error');
      setTimeout(fetchPrices, retryDelay);
    } else {
      showApiError();
      if (TESTING_MODE) showNotification('❌ API unavailable. Using cached/default values', 'error');
    }
  }

  function showApiError() {
    togglePriceUI('error');
  }

  // =========================
  // Refresh scheduler
  // =========================
  function startPriceRefresh() {
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = setInterval(fetchPrices, REFRESH_INTERVAL);
  }

  // =========================
  // Rendering
  // =========================
  function updateAllPrices() {
    const d = getDom();
    if (!d) return;

    // Card prices
    if (d.priceBid) d.priceBid.textContent = formatPrice(currentPrices.bid);
    if (d.priceMid) d.priceMid.textContent = formatPrice(currentPrices.mid);
    if (d.priceAsk) d.priceAsk.textContent = formatPrice(currentPrices.ask);

    // Unit tiles
    if (d.unitOz) d.unitOz.textContent = formatPrice(currentPrices.mid);
    if (d.unitGram) d.unitGram.textContent = formatPrice(currentPrices.mid / TROY_OZ_TO_GRAM);
    if (d.unitKilo) d.unitKilo.textContent = formatPrice(currentPrices.mid / TROY_OZ_TO_KILO);

    // Change
    if (d.changeAmt) {
      const signAmt = currentPrices.change >= 0 ? '+' : '';
      d.changeAmt.textContent = `${signAmt}${formatPrice(currentPrices.change)}`;
      d.changeAmt.classList.toggle('positive', currentPrices.change >= 0);
      d.changeAmt.classList.toggle('negative', currentPrices.change < 0);
    }
    if (d.changePct) {
      const signPct = currentPrices.changePct >= 0 ? '+' : '';
      d.changePct.textContent = `(${signPct}${(Number(currentPrices.changePct) || 0).toFixed(2)}%)`;
    }

    // Table, sticky pill, timestamp
    updatePriceTable();
    updateStickyPill();
    updateLastUpdatedTime();

    // Keep premium calculator spot in sync (if present)
    const { inputs } = d;
    if (inputs.spotInput && isNum(currentPrices.mid)) {
      inputs.spotInput.value = Number(currentPrices.mid).toFixed(2);
    }
    if (window._abRecalcSilverCalcs) window._abRecalcSilverCalcs();
  }

  function updatePriceTable() {
    const d = getDom();
    if (!d.priceTableBody) return;

    const rate = getCurrencyRate(selectedCurrency);
    const units = [
      { name: 'Troy Ounce', factor: 1, unit: 'oz' },
      { name: 'Gram', factor: 1 / TROY_OZ_TO_GRAM, unit: 'g' },
      { name: '10 Grams', factor: 10 / TROY_OZ_TO_GRAM, unit: '10g' },
      { name: 'Pennyweight (dwt)', factor: 0.05, unit: 'dwt' },
      { name: 'Tola', factor: 0.375, unit: 'tola' },
      { name: 'Tael (Hong Kong)', factor: 1.20337, unit: 'tael' },
      { name: 'Kilogram', factor: 1 / TROY_OZ_TO_KILO, unit: 'kg' },
    ];

    let html = '';
    units.forEach((u) => {
      const bid = currentPrices.bid * u.factor * rate;
      const ask = currentPrices.ask * u.factor * rate;
      const mid = currentPrices.mid * u.factor * rate;
      const change = currentPrices.change * u.factor * rate;

      html += `
        <tr data-unit="${u.unit}">
          <td>${u.name}</td>
          <td class="ab-bid">${formatCurrencyPrice(bid, selectedCurrency)}</td>
          <td class="ab-ask">${formatCurrencyPrice(ask, selectedCurrency)}</td>
          <td class="ab-mid">${formatCurrencyPrice(mid, selectedCurrency)}</td>
          <td class="ab-change">${formatCurrencyPrice(change, selectedCurrency, true)}</td>
          <td class="ab-percent ${currentPrices.changePct >= 0 ? 'positive' : 'negative'}">
            ${currentPrices.changePct >= 0 ? '+' : ''}${currentPrices.changePct.toFixed(2)}%
          </td>
        </tr>
      `;
    });

    d.priceTableBody.innerHTML = html;
  }

  function updateLastUpdatedTime() {
    const d = getDom();
    if (!d.lastUpdatedTime) return;

    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    let timestamp = Date.now();
    if (cache) {
      try {
        const { timestamp: ts } = JSON.parse(cache);
        timestamp = ts || timestamp;
      } catch {}
    }

    const date = new Date(timestamp);
    const timeStr =
      date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      }) + ' ET';

    d.lastUpdatedTime.textContent = timeStr;
    d.lastUpdatedTime.setAttribute('datetime', date.toISOString());

    if (TESTING_MODE) {
      const ageMinutes = Math.floor((Date.now() - timestamp) / 60000);
      if (ageMinutes > 0) d.lastUpdatedTime.textContent += ` (${ageMinutes}m ago)`;
    }
  }

  // =========================
  // Currency controls
  // =========================
  function handleCurrencyChange(event) {
    selectedCurrency = event.target.value;
    localStorage.setItem(STORAGE_KEY, selectedCurrency);
    setActiveCurrencyTab(selectedCurrency);
    updatePriceTable();
  }

  function updateCurrencySelector() {
    const d = getDom();
    if (d.currencySelect) d.currencySelect.value = selectedCurrency;
    setActiveCurrencyTab(selectedCurrency);
  }

  function setActiveCurrencyTab(currency) {
    $$('.ab-currency-tabs [data-currency]').forEach((btn) => {
      btn.setAttribute('aria-selected', btn.dataset.currency === currency ? 'true' : 'false');
    });
    const select = $('.ab-currency-select') || byId('ab-currency-selector');
    if (select) select.value = currency;
  }

  function getCurrencyRate(currency) {
    switch (currency) {
      case 'EUR':
        return 1 / currentFX.EURUSD;
      case 'GBP':
        return 1 / currentFX.GBPUSD;
      case 'JPY':
        return currentFX.USDJPY;
      case 'CAD':
        return currentFX.USDCAD;
      case 'AUD':
        return 1 / currentFX.AUDUSD;
      default:
        return 1; // USD
    }
  }

  // =========================
  // Price alerts (demo)
  // =========================
  function handleAlertSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.target);
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      direction: formData.get('direction'),
      price: formData.get('price'),
      metal: 'silver'
    };
    console.log('Price alert (silver):', payload);
    const successDiv = byId('ab-alert-success');
    if (successDiv) {
      successDiv.style.display = 'block';
      setTimeout(() => {
        successDiv.style.display = 'none';
        const container = byId('ab-alert-container');
        if (container) container.style.display = 'none';
        event.target.reset();
      }, 2500);
    }
  }

  // =========================
  // Calculators
  // =========================
  // =========================
// Calculators (robust bindings + first-run compute)
// =========================
function setupCalculators() {
  const $id = (x) => document.getElementById(x);
  const $ = (sel, root = document) => root.querySelector(sel);

  // ----- Portfolio Value (oz / g / kg) -----
  const portfolioAmount =
    $id('ab-portfolio-amount') ||
    $id('portfolio-amount') ||
    $('[data-input="portfolio-amount"]');

  const portfolioUnit =
    $id('ab-portfolio-unit') ||
    $id('portfolio-unit') ||
    $('[data-input="portfolio-unit"]');

  const outPortfolioBid =
    $('[data-calc="portfolio-bid"]') || $id('ab-portfolio-bid');

  const outPortfolioAsk =
    $('[data-calc="portfolio-ask"]') || $id('ab-portfolio-ask');

  const updatePortfolio = () => {
    if (!portfolioAmount || !portfolioUnit) return;
    const amount = parseFloat(portfolioAmount.value) || 0;
    const unit = (portfolioUnit.value || 'oz').toLowerCase();

    // normalize to troy ounces
    let oz = amount;
    if (unit === 'g' || unit === 'gram' || unit === 'grams') oz = amount / TROY_OZ_TO_GRAM;
    if (unit === 'kg' || unit === 'kilo' || unit === 'kilogram') oz = amount / TROY_OZ_TO_KILO;

    const bidValue = oz * (Number(currentPrices.bid) || 0);
    const askValue = oz * (Number(currentPrices.ask) || 0);

    if (outPortfolioBid) outPortfolioBid.textContent = formatPrice(bidValue);
    if (outPortfolioAsk) outPortfolioAsk.textContent = formatPrice(askValue);
  };

  if (portfolioAmount && portfolioUnit) {
    portfolioAmount.addEventListener('input', updatePortfolio);
    portfolioUnit.addEventListener('change', updatePortfolio);
  }

  // ----- Scrap / Jewelry Silver (purity .999, .958, .925, .900, .800) -----
  const scrapWeight =
    $id('ab-scrap-weight') ||
    $id('ab-jewelry-weight') ||
    $id('jewelry-weight') ||
    $('[data-input="jewelry-weight"],[data-input="scrap-weight"]');

  const scrapUnit =
    $id('ab-scrap-unit') ||
    $id('ab-jewelry-unit') ||
    $id('jewelry-unit') ||
    $('[data-input="jewelry-unit"],[data-input="scrap-unit"]');

  const scrapPurity =
    $id('ab-scrap-purity') ||
    $id('ab-silver-purity') ||
    $id('jewelry-fineness') ||
    $('[data-input="scrap-purity"],[data-input="silver-purity"],[data-input="jewelry-fineness"]');

  // outputs (accept both names)
  const outFine =
    $('[data-calc="fine-silver"]') ||
    $id('ab-scrap-fine') ||
    $('[data-calc="fine-metal"]');

  const outScrapValue =
    $('[data-calc="scrap-value"]') ||
    $id('ab-scrap-value') ||
    $('[data-calc="jewelry-value"]');

  const updateScrap = () => {
    if (!scrapWeight) return;
    const weight = parseFloat(scrapWeight.value) || 0;
    const unit = (scrapUnit?.value || 'g').toLowerCase();
    const purity = Math.max(0, Math.min(1, parseFloat(scrapPurity?.value || '0.925'))); // default .925

    // normalize to grams
    const grams = unit === 'oz' ? weight * TROY_OZ_TO_GRAM : unit === 'kg' ? weight * 1000 : weight;
    const fineGrams = grams * purity;
    const ozFine = fineGrams / TROY_OZ_TO_GRAM;
    const value = ozFine * (Number(currentPrices.mid) || 0);

    if (outFine) outFine.textContent = `${fineGrams.toFixed(2)} g`;
    if (outScrapValue) outScrapValue.textContent = formatPrice(value);
  };

  if (scrapWeight) {
    scrapWeight.addEventListener('input', updateScrap);
    scrapUnit?.addEventListener('change', updateScrap);
    scrapPurity?.addEventListener('change', updateScrap);
  }

  // ----- Premium Calculator -----
  const premiumPercent =
    $id('ab-premium-percent') ||
    $id('premium-percent') ||
    $('[data-input="premium-percent"]');

  const spotInput =
    $id('ab-premium-spot') ||
    $id('spot-price') ||
    $('[data-input="spot-price"]');

  const outPremAmt =
    $('[data-calc="premium-amount"]') || $id('ab-premium-amount');

  const outFinalPrice =
    $('[data-calc="final-price"]') || $id('ab-premium-final');

  const updatePremium = () => {
    if (!premiumPercent) return;
    const premium = parseFloat(premiumPercent.value) || 0;
    const spot = spotInput ? (parseFloat(spotInput.value) || Number(currentPrices.mid) || 0) : (Number(currentPrices.mid) || 0);
    const premAmt = spot * (premium / 100);
    const final = spot + premAmt;

    if (outPremAmt) outPremAmt.textContent = formatPrice(premAmt);
    if (outFinalPrice) outFinalPrice.textContent = formatPrice(final);
  };

  if (premiumPercent) {
    premiumPercent.addEventListener('input', updatePremium);
    spotInput?.addEventListener('input', updatePremium);
  }

  // Expose a recalculator so price updates can refresh calculator outputs
  window._abRecalcSilverCalcs = () => {
    updatePortfolio();
    updateScrap();
    updatePremium();
  };

  // First-run compute so numbers change immediately
  window._abRecalcSilverCalcs();
}


  // =========================
  // Sticky price pill
  // =========================
  function setupStickyPill() {
    const d = getDom();
    const pill = d.stickyPill;
    if (!pill) return;

    let ticking = false;

    function updatePillVisibility() {
      const priceCard = $('.ab-price-card', getSection());
      if (!priceCard) return;

      const scrollPos = window.scrollY;
      const cardBottom = priceCard.offsetTop + priceCard.offsetHeight;
      if (scrollPos > cardBottom) {
        pill.classList.add('active');
        if (pill.style.display === 'none') pill.style.display = '';
      } else {
        pill.classList.remove('active');
      }
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(updatePillVisibility);
        ticking = true;
      }
    }

    if (window.innerWidth <= 768) {
      window.addEventListener('scroll', requestTick);
      window.addEventListener('resize', requestTick);
    }
  }

  function updateStickyPill() {
    const d = getDom();
    if (d.stickyPrice) d.stickyPrice.textContent = formatPrice(currentPrices.mid);
    if (d.stickyChange) {
      const sign = currentPrices.changePct >= 0 ? '+' : '';
      d.stickyChange.textContent = `${sign}${(Number(currentPrices.changePct) || 0).toFixed(2)}%`;
      d.stickyChange.classList.toggle('positive', currentPrices.changePct >= 0);
      d.stickyChange.classList.toggle('negative', currentPrices.changePct < 0);
    }
  }

  // =========================
  // Utilities
  // =========================
  function formatPrice(value) {
    return '$' + Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function formatCurrencyPrice(value, currency, showSign = false) {
    const symbols = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$' };
    const symbol = symbols[currency] || '$';
    const decimals = currency === 'JPY' ? 0 : 2;
    const abs = Math.abs(value).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const sign = showSign && value !== 0 ? (value > 0 ? '+' : '-') : '';
    return sign + symbol + abs;
  }

  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  const num = (v, fallback) => (isNum(v) ? v : fallback);

  function dispatchUpdateEvent() {
    try {
      window.dispatchEvent(
        new CustomEvent('ab:silverPriceUpdated', {
          detail: {
            prices: { ...currentPrices },
            currency: selectedCurrency,
            fx: { ...currentFX }
          }
        })
      );
    } catch {}
  }

  // =========================
  // Boot
  // =========================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
